import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchUser = useCallback(async () => {
        try {
            const response = await api.get('/auth/me');
            setUser(response.data?.data ?? null);
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    useEffect(() => {
        const handleUnauthorized = () => {
            setUser(null);
        };
        window.addEventListener('auth:unauthorized', handleUnauthorized);
        return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
    }, []);

    const login = async (identifier, password) => {
        await api.get('/sanctum/csrf-cookie', { baseURL: '' });
        const response = await api.post('/auth/login', { identifier, password });
        setUser(response.data.data.user);
        return response.data;
    };

    const logout = async () => {
        await api.post('/auth/logout');
        setUser(null);
    };

    const hasRole = (role) => {
        if (!user) return false;
        return user.roles?.some(r => (typeof r === 'string' ? r : r.name) === role);
    };

    const isSuperadmin = () => hasRole('superadmin');
    const isOwner = () => hasRole('owner');
    const isCustomer = () => hasRole('customer');

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            login,
            logout,
            fetchUser,
            hasRole,
            isSuperadmin,
            isOwner,
            isCustomer,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
